/**
 * The notification sound (web and desktop): this device's setting (on/off + which sound, in localStorage, like the
 * theme), and playback through Web Audio. packages/core logic/notify `soundDecision` decides whether an event rings
 * (notifier.ts asks); the sounds are /sounds/<name>.wav (scripts/sounds/generate.ts).
 *
 * - The Mac app plays them natively (`sound_play` / `sound_ring`, apps/desktop/src-tauri/src/sound.rs): its webview
 *   keeps Web Audio locked until a click or key press in that window, and Bundle mostly rings in the background.
 * - One source of sound: the desktop app's OS notifications are sent without a sound (notifier.ts), and browser
 *   notifications shown here are `silent`, so a notification rings once, from here.
 * - Autoplay: browsers (and WKWebView) keep an AudioContext suspended until the person interacts with the page, so the
 *   first pointer/key press anywhere resumes it. Until then a notification can't ring (logged, not an error).
 * - Bursts and several tabs/windows: the last time this device rang is shared through localStorage, so a burst rings
 *   once (SOUND_THROTTLE_MS) even with Bundle open in more than one tab or window.
 * - Each time it rings it dispatches `bundle:notification-sound` on window ({ sound, preview }) — what tests and the
 *   mock-mode check listen to, since nobody can hear a headless browser.
 * - The huddle ringtone (/sounds/ringtone.wav, scripts/sounds/import.ts) loops while an incoming huddle rings
 *   (`startRingtone`, IncomingHuddle.tsx), like Google Meet's incoming call; it ignores the notification sound setting,
 *   as the phone apps' ring does. One tab or window rings at a time (a Web Lock). It dispatches `bundle:ringtone`
 *   ({ ringing }) when it starts and stops.
 */
import type { NotificationEvent } from "@bundle/contracts";
import { Notify } from "@bundle/core";
import { isDesktop, notificationsOs } from "./desktop.ts";

const SETTING_KEY = "bundle.notificationSound.v1";
const LAST_PLAYED_KEY = "bundle.notificationSound.lastPlayedAt";
const RINGTONE_LOCK = "bundle.ringtone";

/** The Mac app: sounds play in the app itself, not the webview (see above). */
function native(): boolean {
  return isDesktop() && notificationsOs() === "macos";
}

function invokeNative(cmd: "sound_play" | "sound_ring", args: Record<string, unknown>): void {
  void import("@tauri-apps/api/core")
    .then(({ invoke }) => invoke(cmd, args))
    .catch((err: unknown) => console.warn(cmd, err));
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private storage: kept in memory for this session only.
  }
}

export class NotificationSoundPlayer {
  private setting: Notify.SoundSetting;
  private lastPlayed: number | undefined;
  private readonly listeners = new Set<() => void>();
  private context: AudioContext | undefined;
  private readonly buffers = new Map<string, Promise<AudioBuffer>>();

  constructor() {
    let stored: unknown = null;
    try {
      stored = JSON.parse(read(SETTING_KEY) ?? "null");
    } catch {
      stored = null;
    }
    this.setting = Notify.parseSoundSetting(stored);
    if (typeof window === "undefined") return;
    // Autoplay: unlock on the first interaction (and again if the system suspended the context meanwhile).
    const unlock = () => {
      const ctx = this.audio();
      if (ctx?.state === "suspended") void ctx.resume().catch(() => undefined);
    };
    for (const type of ["pointerdown", "keydown", "touchend"]) window.addEventListener(type, unlock, { capture: true, passive: true });
    window.addEventListener("storage", (e) => {
      if (e.key !== SETTING_KEY) return;
      this.setting = Notify.parseSoundSetting(safeParse(e.newValue));
      this.emit();
    });
  }

  // ---- Setting (Settings → Notifications) ----

  readonly subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  readonly getSnapshot = (): Notify.SoundSetting => this.setting;

  set(setting: Notify.SoundSetting): void {
    this.setting = setting;
    write(SETTING_KEY, JSON.stringify(setting));
    this.emit();
  }

  /** Plays a sound now, ignoring the setting and throttle (the picker's preview — a click, so audio is unlocked). */
  preview(sound: Notify.NotificationSound): void {
    void this.play(sound, true);
  }

  // ---- Ringing for a notification ----

  /** Rings for a `notification` event when `soundDecision` says so. */
  ring(event: NotificationEvent, ctx: Omit<Notify.SoundContext, "setting" | "now" | "lastPlayedAt">): Notify.SoundDecision {
    const now = Date.now();
    const shared = Number(read(LAST_PLAYED_KEY));
    const lastPlayedAt = Math.max(this.lastPlayed ?? 0, Number.isFinite(shared) ? shared : 0) || undefined;
    const decision = Notify.soundDecision(event, { ...ctx, setting: this.setting, now, lastPlayedAt });
    if (decision.play) {
      this.lastPlayed = now;
      write(LAST_PLAYED_KEY, String(now));
      void this.play(decision.sound, false);
    }
    return decision;
  }

  // ---- Huddle ringtone ----

  /**
   * Loops the ringtone until the returned function is called (answered, declined, timed out, or answered elsewhere:
   * the incoming ring went away). With Bundle open in several tabs or windows, the first one rings and the others wait
   * for its lock (so a tab that stops ringing hands over only while the ring is still on there).
   */
  startRingtone(): () => void {
    if (typeof window === "undefined") return () => undefined;
    const abort = new AbortController();
    let source: AudioBufferSourceNode | undefined;
    let nativeRinging = false;
    const ring = async (): Promise<void> => {
      if (native()) {
        if (abort.signal.aborted) return;
        nativeRinging = true;
        invokeNative("sound_ring", { on: true });
        window.dispatchEvent(new CustomEvent("bundle:ringtone", { detail: { ringing: true } }));
        return;
      }
      const ctx = this.audio();
      if (ctx === undefined || abort.signal.aborted) return;
      // Locked by autoplay: the source starts anyway and is heard from the first click or key press.
      if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);
      try {
        const buffer = await this.buffer(ctx, "ringtone");
        if (abort.signal.aborted) return;
        source = ctx.createBufferSource();
        source.buffer = buffer;
        source.loop = true;
        source.connect(ctx.destination);
        source.start();
        window.dispatchEvent(new CustomEvent("bundle:ringtone", { detail: { ringing: true } }));
      } catch (err) {
        console.warn("ringtone failed", err);
      }
    };
    const stopped = new Promise<void>((resolve) => abort.signal.addEventListener("abort", () => resolve(), { once: true }));
    const locks = (navigator as Navigator & { locks?: LockManager }).locks;
    if (locks === undefined) void ring();
    else {
      locks
        .request(RINGTONE_LOCK, { signal: abort.signal }, async () => {
          await ring();
          await stopped;
        })
        .catch(() => undefined); // aborted while waiting for another tab's ring
    }
    return () => {
      if (abort.signal.aborted) return;
      abort.abort();
      if (nativeRinging) {
        nativeRinging = false;
        invokeNative("sound_ring", { on: false });
        window.dispatchEvent(new CustomEvent("bundle:ringtone", { detail: { ringing: false } }));
        return;
      }
      if (source === undefined) return;
      source.stop();
      source.disconnect();
      source = undefined;
      window.dispatchEvent(new CustomEvent("bundle:ringtone", { detail: { ringing: false } }));
    };
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  private audio(): AudioContext | undefined {
    if (this.context !== undefined) return this.context;
    const Ctor = window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Ctor === undefined) return undefined;
    this.context = new Ctor();
    return this.context;
  }

  private buffer(ctx: AudioContext, sound: Notify.NotificationSound | "ringtone"): Promise<AudioBuffer> {
    let p = this.buffers.get(sound);
    if (p === undefined) {
      p = fetch(`/sounds/${sound}.wav`)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`sound ${sound}: ${r.status}`))))
        .then((bytes) => ctx.decodeAudioData(bytes));
      p.catch(() => this.buffers.delete(sound));
      this.buffers.set(sound, p);
    }
    return p;
  }

  private async play(sound: Notify.NotificationSound, preview: boolean): Promise<void> {
    window.dispatchEvent(new CustomEvent("bundle:notification-sound", { detail: { sound, preview } }));
    if (native()) {
      invokeNative("sound_play", { name: sound });
      return;
    }
    const ctx = this.audio();
    if (ctx === undefined) return;
    // Not unlocked yet: resume() stays pending until an interaction, so don't wait for it long.
    if (ctx.state === "suspended") await Promise.race([ctx.resume().catch(() => undefined), new Promise((r) => setTimeout(r, 250))]);
    if (ctx.state !== "running") {
      console.info("notification sound skipped: audio is locked until the first click or key press");
      return;
    }
    try {
      const source = ctx.createBufferSource();
      source.buffer = await this.buffer(ctx, sound);
      source.connect(ctx.destination);
      source.start();
    } catch (err) {
      console.warn("notification sound failed", err);
    }
  }
}

function safeParse(s: string | null): unknown {
  try {
    return JSON.parse(s ?? "null");
  } catch {
    return null;
  }
}

/** The app's one player (notifier.ts rings it; Settings → Notifications edits it). */
export const notificationSound = new NotificationSoundPlayer();
